import React from 'react'
import qs from 'qs'
import { compose, withHandlers, lifecycle, withProps, withStateHandlers } from 'recompose'
import { connect } from 'react-redux'
import { show } from 'redux-modal'
import { reduxForm, change, initialize } from 'redux-form'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import withScrollToAnchor from 'components/common/hoc/withScrollToAnchor'
import { getCurrentStore, getPageLocation } from 'selectors'
import { getAppointments } from 'selectors/getAppointments'
import { getValues as getFormValues } from 'components/Appointment/selectors'
import { showStoreLocatorModalInAppointmentMode, showStoreLocatorModal, selectMyStore } from 'actions/storeLocator'
import {
  setServicesList,
  setQuoteProductAndServices,
  setPreSelectedServices,
  setActivePromotions
} from '../../../actions'
import { getAppointmentDatesAndSlots } from '../../../Stepper/actions'
import validate from './validate'
import shuffle from 'helpers/shuffle'
import { generateAddress, getQueryParamValue } from 'helpers/storeLocator'
import { composeFormattedPhoneNumberFromObject } from 'helpers/phone-number'
import { getAutoSelectedServicesInCategories, getCategories, getServicesPickerItems } from '../../../BOTServicesSelector/helpers'
import {
  getServicesData,
  formatPopularServiceItems,
  getPromotionsData,
  getPromoExcludedData
} from './helpers'
import StoreAndServices from './StoreAndServices'
import {
  APPOINTMENT_NOTES_QUERY_PARAMETER,
  MORE_SERVICES_ID,
  POPULAR_SERVICES_ID,
  STORE_NOT_ELIGIBLE_FOR_PROMOTION_MODAL
} from '../../constants'
import {
  APPOINTMENT_FORM_NAME,
  DATE_TIME_SELECTOR_FIELD_NAME,
  DROP_OFF_TIME_FIELD_NAME,
  ACTIVE_PROMOTIONS_FIELD_NAME,
  SERVICES_SELECTOR_FIELD_NAME
} from 'constant'
import { getRemovedPromotions } from 'components/QuickAppointmentPage/selectors'
import { getIsOnlineAppointment } from 'components/QuickAppointmentPage/BOTAppointmentSummary/helpers'
import model from 'components/QuickAppointmentPage/viewModel.js'

const withStoreAndServicesComponentState = withStateHandlers(
  {
    showStoreDetails: false,
    submitAttemptFailed: false,
    servicesDrawerExpanded: null,
    drawerButtonRef: React.createRef()
  },
  {
    setShowStoreDetails: () => showStoreDetails => ({ showStoreDetails }),
    setSubmitAttemptFailed: () => submitAttemptFailed => ({ submitAttemptFailed }),
    setServicesDrawerExpanded: () => servicesDrawerExpanded => ({ servicesDrawerExpanded })
  })

const mapStateToProps = (state, props) => {
  const myStore = getCurrentStore(state) || {}
  const { address: { phoneNumber = {}} = {}, storeNumber } = myStore
  const [BOTServicesSelector, BOTStoreLocation] = props.children
  const { popularServices, servicesCategory } = getServicesData({ ...BOTServicesSelector })
  const { promoExcluded } = getPromoExcludedData({ ...BOTServicesSelector })
  const {
    catalogRefIds: selectedServices,
    appointmentDate,
    [DROP_OFF_TIME_FIELD_NAME]: timeDropdownValue
  } = getFormValues(state) || {}
  const { servicesList } = getAppointments(state)
  const location = getPageLocation(state)
  const preExpandedServiceQuery = qs.parse(location.search.slice(1)) || {}

  return {
    address: generateAddress(myStore),
    formattedNumber: composeFormattedPhoneNumberFromObject(phoneNumber),
    popularServiceItems: formatPopularServiceItems(popularServices),
    servicesCategories: getCategories({ selectedServices, servicesCategory }),
    servicesDrawerItems: shuffle.halfs(getServicesPickerItems({ servicesCategory })),
    nullState: isEmpty(myStore),
    appointmentDate,
    timeDropdownValue,
    BOTStoreLocation,
    BOTServicesSelector,
    servicesList,
    selectedServices,
    promoExcluded,
    categoryQueryParam: preExpandedServiceQuery.preExpandedService || '',
    removedPromotions: getRemovedPromotions(state),
    isOnlineAppointment: getIsOnlineAppointment(location),
    storeNumber,
    appointmentNotes: getQueryParamValue(APPOINTMENT_NOTES_QUERY_PARAMETER)
  }
}

const withStoreLocatorHandlers = withHandlers({
  handleEditClick: ({
    showStoreLocatorModal,
    isOnlineAppointment,
    promoExcluded
  }) => () => (isOnlineAppointment && !promoExcluded)
    ? showStoreLocatorModal({ isAppointments: true })
    : showStoreLocatorModal(),
  handleCloseClick: ({ setShowStoreDetails }) => () => setShowStoreDetails(false)
})

const withAnchorProps = withProps(({ servicesDrawerExpanded }) => ({
  targetId: servicesDrawerExpanded !== null ?
    servicesDrawerExpanded ?
      POPULAR_SERVICES_ID :
      MORE_SERVICES_ID :
    null,
  padding: 20
}))

const withHandleMoreServicesClick = withHandlers({
  handleMoreServicesClick: ({ setServicesDrawerExpanded }) => expanded => setServicesDrawerExpanded(expanded)
})

const withOnSubmit = withHandlers({
  onSubmit: ({
    beforeNavigatingToDateTimeStep,
    nextPageClick,
    setSubmitAttemptFailed,
    submitAttemptFailed
  }) => () => {
    beforeNavigatingToDateTimeStep({ setSubmitAttemptFailed, submitAttemptFailed })
    nextPageClick()
  }
})

const withLifecycle = lifecycle({
  componentDidMount() {
    const {
      setServicesList,
      setQuoteProductAndServices,
      setPreSelectedServices,
      change,
      appointmentDate,
      timeDropdownValue,
      BOTServicesSelector,
      drawerButtonRef,
      categoryQueryParam,
      setActivePromotions,
      initialize,
      promoExcluded,
      removedPromotions,
      selectedServices,
      show,
      address,
      selectMyStore,
      storeNumber
    } = this.props
    const { servicesCategory } = getServicesData({ ...BOTServicesSelector })
    const { promotionsDetails } = getPromotionsData({ ...BOTServicesSelector })

    appointmentDate && change(APPOINTMENT_FORM_NAME, DATE_TIME_SELECTOR_FIELD_NAME, null)
    timeDropdownValue && change(APPOINTMENT_FORM_NAME, DROP_OFF_TIME_FIELD_NAME, null)
    setQuoteProductAndServices()
    setServicesList(servicesCategory)

    // On page load, when user lands to Quick Appointment page from the promotion page,
    // we are checking "promoExcluded" attribute from the cartridge.
    if (!promoExcluded) {
      // If promotion eligible for the active store, we are checking is this promo was deleted by user earlier.
      // For example user removed promo for selected service and goes to the step 2 or 3,
      // and after that he goes back to step 1. In this case we don't want to show him removed promo.
      if (removedPromotions.length) {
        const isSelectedServicesContainsRemovedPromo = selectedServices.some(service =>
          removedPromotions.some(promo => service === promo.serviceSkuId))

        if (isSelectedServicesContainsRemovedPromo) {
          const promosWithoutPromoThatWasRemoved = promotionsDetails.filter(promo =>
            !removedPromotions.some(removedPromo => removedPromo.serviceSkuId === promo.serviceSkuId))

          setActivePromotions(promosWithoutPromoThatWasRemoved)
          initialize({ [ACTIVE_PROMOTIONS_FIELD_NAME]: promosWithoutPromoThatWasRemoved })
        } else {
          const promosWithoutPromoThatWasRemoved = promotionsDetails.filter(promo =>
            removedPromotions.some(removedPromo => removedPromo.serviceSkuId !== promo.serviceSkuId))

          setActivePromotions(promosWithoutPromoThatWasRemoved)
          initialize({ [ACTIVE_PROMOTIONS_FIELD_NAME]: promosWithoutPromoThatWasRemoved })
        }
      } else {
        const filetedPromotions = promotionsDetails.filter(promo =>
          selectedServices.some(service => service === promo.serviceSkuId))

        setActivePromotions(filetedPromotions)
        initialize({ [ACTIVE_PROMOTIONS_FIELD_NAME]: filetedPromotions })
      }
    } else {
      show(STORE_NOT_ELIGIBLE_FOR_PROMOTION_MODAL, {
        selectStore: () => selectMyStore({ storeNumber, dontRefreshThePage: true }),
        storeAddress: address
      })
    }

    const autoSelectedServices = getAutoSelectedServicesInCategories(servicesCategory)
    // Store preselected services in Redux store. Simulate a click to expand a list of services
    if (autoSelectedServices && autoSelectedServices.length) {
      setPreSelectedServices(autoSelectedServices)
      drawerButtonRef.current.click()
    }

    if (categoryQueryParam) {
      drawerButtonRef.current.click()
    }
  },
  componentDidUpdate({
    targetId: prevTargetId,
    promoExcluded: prevPromoExcluded,
    selectedServices: prevSelectedServices
  }) {
    const {
      servicesList,
      setServicesList,
      BOTServicesSelector,
      targetId,
      scrollToAnchorHandler,
      promoExcluded,
      setActivePromotions,
      change,
      selectedServices,
      removedPromotions,
      appointmentNotes
    } = this.props
    const { servicesCategory } = getServicesData({ ...BOTServicesSelector })
    const { submitAttemptFailed, setSubmitAttemptFailed, nullState } = this.props
    const { promotionsDetails } = getPromotionsData({ ...BOTServicesSelector })
    const autoSelectedServices = getAutoSelectedServicesInCategories(servicesCategory)

    isEmpty(servicesList) && !isEmpty(servicesCategory) && setServicesList(servicesCategory)
    !nullState && submitAttemptFailed && setSubmitAttemptFailed(false)
    prevTargetId !== targetId && setTimeout(() => scrollToAnchorHandler())

    if (!isEqual(promoExcluded, prevPromoExcluded) && promoExcluded) {
      setActivePromotions([])
      change(APPOINTMENT_FORM_NAME, ACTIVE_PROMOTIONS_FIELD_NAME, [])
    }

    if (!isEqual(promoExcluded, prevPromoExcluded) && !promoExcluded) {
      setActivePromotions(promotionsDetails)
      initialize({ [ACTIVE_PROMOTIONS_FIELD_NAME]: promotionsDetails })

      if (autoSelectedServices && autoSelectedServices.length && !selectedServices.includes(autoSelectedServices[0])) {
        change(APPOINTMENT_FORM_NAME, SERVICES_SELECTOR_FIELD_NAME, [...selectedServices, ...autoSelectedServices])
      }
    }

    const isPromoWasRemovedEarlier = removedPromotions.some(({ serviceSkuId }) =>
      selectedServices.includes(serviceSkuId))

    const updatePromosStateAndFormField = ({ promotionsDetails, selectedServices }) => {
      const filteredPromotions = promotionsDetails.filter(({ serviceSkuId }) => selectedServices.includes(serviceSkuId))

      setActivePromotions(filteredPromotions)
      change(APPOINTMENT_FORM_NAME, ACTIVE_PROMOTIONS_FIELD_NAME, filteredPromotions)
    }

    if (!isEqual(selectedServices, prevSelectedServices)
        && (selectedServices.length > prevSelectedServices.length)
        && !promoExcluded
        && isPromoWasRemovedEarlier) {
      const serviceThatWasSelected = selectedServices.filter(service =>
        !promotionsDetails.some(promo => service === promo.serviceSkuId))

      if (selectedServices.includes(serviceThatWasSelected)) {
        updatePromosStateAndFormField({ selectedServices, promotionsDetails })
      }
    }

    if (!isEqual(selectedServices, prevSelectedServices) && !promoExcluded && !isPromoWasRemovedEarlier) {
      updatePromosStateAndFormField({ selectedServices, promotionsDetails })
    }

    if (appointmentNotes && !isEqual(selectedServices, prevSelectedServices) && !isEmpty(selectedServices)) {
      selectedServices.includes(autoSelectedServices[0])
        ? change(APPOINTMENT_FORM_NAME, model.serviceComment.name, appointmentNotes)
        : change(APPOINTMENT_FORM_NAME, model.serviceComment.name, '')
    }
  }
})

export default compose(
  withStoreAndServicesComponentState,
  connect(mapStateToProps, {
    getAppointmentDatesAndSlots,
    showStoreLocatorModalInAppointmentMode,
    showStoreLocatorModal,
    setServicesList,
    setQuoteProductAndServices,
    setPreSelectedServices,
    change,
    setActivePromotions,
    initialize,
    show,
    selectMyStore
  }),
  withStoreLocatorHandlers,
  withAnchorProps,
  withScrollToAnchor,
  withHandleMoreServicesClick,
  withOnSubmit,
  withLifecycle,
  reduxForm({
    validate,
    form: APPOINTMENT_FORM_NAME,
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true
  })
)(StoreAndServices)
